import { apiErrorMessage } from '@agos/api-client'
import {
  getGetAuthMeProfileQueryKey,
  getGetEmployeesMyDeliveriesQueryKey,
  getGetEmployeesMyWalletQueryKey,
  useGetEmployeesMyDeliveries,
  useGetEmployeesMyWallet,
  usePatchAuthMeProfile,
  usePatchEmployeesMyAvailability,
  usePatchEmployeesMyVehicle,
  useGetAuthMeProfile,
} from '@agos/api-client/hr'
import { useGetSettingsDelivery } from '@agos/api-client/platform'
import {
  useGetVehicles,
  usePatchOrdersOrderId,
  usePostOrdersOrderIdConfirmStatus,
} from '@agos/api-client/orders'
import { useGetSettingsQrPaymentActive } from '@agos/api-client/payments'
import {
  alsoDrivesOf,
  formatCents,
  formatCount,
  isExternalOf,
  MAP_APP_LABELS,
  MAP_APPS,
  paymentMethodBadge,
  T,
  ThemeToggle,
  tt,
  useAuth,
} from '@agos/shared'
import { ConfirmDialog, Spinner, cn, useToast } from '@agos/ui'
import { useQueryClient } from '@tanstack/react-query'
import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  CircleCheck,
  Clock,
  MapPin,
  Navigation,
  Phone,
  LayoutDashboard,
  LogOut,
  Power,
  QrCode,
  RefreshCw,
  Truck,
  Undo2,
  User,
  Wallet,
} from 'lucide-react'
import { useMemo, useState } from 'react'
import { Link } from 'wouter'

import { CourierPanels, type CourierTab } from '../courier/CourierPanels'
import { courierDoorChoices } from '../courier/courier'
import { TransferPaymentSheet } from '../qr/TransferPaymentSheet'
import { offersQr } from '../qr/qr'
import {
  buzz,
  currentVehicle,
  collectLabel,
  collectOutcome,
  collectTone,
  deliveredPayload,
  closeToast,
  doorChoiceLabel,
  doorPaymentChoices,
  doorPaymentPreselect,
  doorStep,
  drivingExits,
  goingToAddressPayload,
  deliveriesOf,
  elapsedLabel,
  emptyBoardNote,
  emptyBoardReason,
  minutesWaiting,
  navigationUrl,
  orderedDeliveries,
  runTotals,
  shiftLabel,
  statusLabel,
  telUrl,
  handBackNote,
  handBackReason,
  isBankSettled,
  mayHandBack,
  urgencyOf,
  walletNote,
  vehicleLabel,
  vehicleNote,
  vehiclesOf,
  walletOf,
  mapAppOf,
  mapAppPayload,
  mapChoiceNote,
  preferredMapApp,
  choiceKeptNote,
  qrChip,
  unconfirmedChip,
  type Delivery,
} from './driving'

/**
 * `/driving` — §9.6's driving surface, and the destination §9.1's driver tile routes to.
 *
 * Fullscreen and phone-first: no sidebar, no header, one column at every width, every action a full-width
 * control a thumb can hit. A driver holds the phone in one hand.
 *
 * **FB-180** is why none of `driver.tsx`'s wiring is here — see `driving.ts` and the module spec. In short:
 * it filtered drivers on a column that does not exist, matched orders on the wrong one, and had no
 * authentication at all. The signed-in employee is the driver; the server scopes every request to them.
 *
 * The 15-second poll is legacy's interval, copied exactly (§14.1).
 */

const POLL_MS = 15_000

/**
 * Whether the three run figures are drawn above the list - §9.384, FB-683.
 *
 * **False on the owner's instruction, and a constant rather than a deletion:** *"hide it for now, do
 * not fully remove it… maybe we will give it back later."* The block below is still compiled and
 * still type-checked against `totals`, so it cannot drift out of date while it is off, and turning
 * it back on is this one word.
 */
const SHOW_RUN_TOTALS = false

export function DrivingPage() {
  const { toast } = useToast()
  // §9.684 - what this driver may open decides which ways into the system are offered, and `logout` is the
  // way out of a surface that draws no sidebar (§9.162). §9.759 - `user`, because an administrator opens this
  // screen by role and their empty board has to say whether the dispatcher can ever give them a delivery.
  const { hasPermission, logout, user } = useAuth()
  /* §9.786, BACKEND.md §10A.438 - an external courier: no ways into the restaurant's screens (the server refuses
   * him every one), no fleet van (he drives his own car), and the pool, his orders and his earnings above. */
  const external = isExternalOf(user)
  const [courierTab, setCourierTab] = useState<CourierTab>('pool')
  const exits = external ? [] : drivingExits(hasPermission)
  /* §9.786 - the door question, less the card terminal a courier does not carry. */
  const doorChoices = (delivery: Delivery) =>
    external ? courierDoorChoices(doorPaymentChoices(delivery)) : doorPaymentChoices(delivery)
  const queryClient = useQueryClient()
  const [confirming, setConfirming] = useState<Delivery | null>(null)
  /* §9.609 - which of the two door methods the customer actually handed over. Seeded from the order when the
   * dialog opens, because the order already carries what they declared when it was taken (`card_courier` is
   * 46 % of this franchise's deliveries), so a customer who does what they said costs the driver one tap.
   * §9.751 - seeded by `doorPaymentPreselect`, which opens an unconfirmed online order on nothing. */
  const [paidBy, setPaidBy] = useState<string>('')
  /**
   * §9.370 - the order the customer would not take.
   *
   * Separate from `confirming` because they are different acts with different consequences, and one
   * dialog for both is how somebody confirms the wrong one.
   */
  const [handingBack, setHandingBack] = useState<Delivery | null>(null)
  const [paying, setPaying] = useState<Delivery | null>(null)
  // Stamped per render pass rather than per row, so a list cannot disagree with itself while it is read.
  const [openedAt] = useState(() => Date.now())

  const deliveryQuery = useGetEmployeesMyDeliveries({ query: { refetchInterval: POLL_MS } })
  /* §9.518, FB-801 - **the setting the owner asked for twice, which this page never read.** `navigationUrl`
     defaulted to Google for every franchise while the select saved and validated, because its second argument
     was optional and the one call site passed nothing. Its own query rather than a prop: the setting belongs
     to the franchise and is used here, and react-query caches it across the polling the board does every few
     seconds so it is fetched once rather than with every refresh of the deliveries. */
  const deliverySettings = useGetSettingsDelivery()
  /* §9.519, owner O30 - the driver's own choice wins, then the franchise's, then Google. The profile read is
     the same one the manager's own profile screen uses, and react-query shares it. */
  const myProfile = useGetAuthMeProfile()
  /* §9.639, FB-965 - named rather than inlined, because the select and the note below need the two answers
     separately: which map *this driver* asked for, and which one the franchise would supply if they asked for
     none. The resolution order is unchanged and is still the whole of §9.519's rule. */
  const myMapApp = mapAppOf(myProfile.data)
  const franchiseMapApp = mapAppOf(deliverySettings.data)
  const mapApp = preferredMapApp(myMapApp, franchiseMapApp)
  const walletQuery = useGetEmployeesMyWallet()
  const vehicleQuery = useGetVehicles(undefined, { query: { enabled: !external } })
  // Public by design — "the driver's app asks before it knows who is signed in, and the answer names
  // nothing but a bank".
  const qrActiveQuery = useGetSettingsQrPaymentActive()
  // The cast this carried is gone: `/settings/qr-payment/active` is typed now (BLOCKED #4), so the
  // generated client answers `QrPaymentAvailability` and the shape is the backend's to state.
  const qrActive = qrActiveQuery.data?.isActive === true

  const deliveries = useMemo(
    () => orderedDeliveries(deliveriesOf(deliveryQuery.data)),
    [deliveryQuery.data],
  )
  const wallet = useMemo(() => walletOf(walletQuery.data), [walletQuery.data])
  const vehicles = useMemo(() => vehiclesOf(vehicleQuery.data), [vehicleQuery.data])
  const vehicle = currentVehicle(wallet, vehicles)
  const totals = runTotals(deliveries)
  /* §9.759, FB-1078 - why the board is empty, decided by the rule: an administrator who is not marked *Jazdí aj
   * rozvoz* is told the dispatcher will never assign them anything, which a driver or a marked person never reads. */
  const emptyReason = emptyBoardReason(wallet, { systemRole: user?.systemRole, alsoDrives: alsoDrivesOf(user) })
  /* §9.751, FB-1067 - what a lock will keep - a live receipt, or a payment the bank already took - for the choice
   * the driver has made in the open dialog. */
  const keepsNote = confirming === null ? '' : choiceKeptNote(confirming, paidBy)

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: getGetEmployeesMyDeliveriesQueryKey() })
    void queryClient.invalidateQueries({ queryKey: getGetEmployeesMyWalletQueryKey() })
  }

  const setVehicle = usePatchEmployeesMyVehicle({
    mutation: {
      onSuccess: () => {
        refresh()
        toast({ title: tt('Vozidlo prepnuté') })
      },
      // §9.385 - a vehicle somebody else is on, or one that was retired, is named by the server.
      onError: (caught) =>
        toast({
          variant: 'destructive',
          title: tt('Vozidlo sa nepodarilo prepnúť'),
          description: apiErrorMessage(caught, tt('Server zmenu odmietol.')),
        }),
    },
  })

  /* §9.639, FB-965 - *"add settings in rozvozár app, where they can change it to their preferred"*. The same
     endpoint the manager's profile screen writes, with **one key**: `ProfileService.update` is
     `body.containsKey` per field, so nothing this body omits is touched and a driver changing their map cannot
     blank their own name.
     **The profile query is what has to be invalidated**, not `refresh()`: the deliveries and the wallet say
     nothing about a map, and the links read `myMapApp` off this read - so without it every *Navigovať* on
     screen keeps opening the old app until a reload, which is the difference between a setting and a setting a
     driver believes. */
  const setMapApp = usePatchAuthMeProfile({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: getGetAuthMeProfileQueryKey() })
        toast({ title: tt('Mapa prepnutá') })
      },
      // §9.385 - the server validates the value against the five it knows, so let it name the refusal.
      onError: (caught) =>
        toast({
          variant: 'destructive',
          title: tt('Mapu sa nepodarilo prepnúť'),
          description: apiErrorMessage(caught, tt('Server zmenu odmietol.')),
        }),
    },
  })

  const setShift = usePatchEmployeesMyAvailability({
    mutation: {
      onSuccess: () => {
        refresh()
        toast({ title: tt('Smena prepnutá') })
      },
      onError: (caught) =>
        toast({
          variant: 'destructive',
          title: tt('Smenu sa nepodarilo prepnúť'),
          description: apiErrorMessage(caught, tt('Server zmenu odmietol.')),
        }),
    },
  })

  const deliver = usePostOrdersOrderIdConfirmStatus({
    mutation: {
      /* §9.751, FB-1067 - the answer names the method the order holds after the close (BACKEND.md §10A.405), and
       * the toast compares it with what this close sent: a printed receipt keeps its method, and *Doručené* alone
       * said nothing about a choice the server kept out. */
      onSuccess: (answer, variables) => {
        setConfirming(null)
        setHandingBack(null)
        refresh()
        // §9.751, FB-1069 - the hand-back shares this mutation, and it is not a delivery.
        toast(closeToast(variables.data, answer))
      },
      // §9.385 - **the one a driver reads at a door.** `confirm-status` refuses a handover by name
      // (an order already closed, one not assigned to this driver), and *"skúste to znova"* is advice
      // that cannot be right for every one of those.
      onError: (caught) => {
        setConfirming(null)
        setHandingBack(null)
        toast({
          variant: 'destructive',
          title: tt('Stav sa nezmenil'),
          description: apiErrorMessage(
            caught,
            tt('Skúste to znova, alebo zavolajte dispečerovi.'),
          ),
        })
      },
    },
  })

  /* §9.682, owner #23b, FB-1000 - *Ísť na adresu*, the step the port dropped. Legacy's app sent exactly this
   * `PATCH /orders/:id` with `{ status: 'at_address' }`, and the server sends the on-the-way text on that
   * transition after its commit and once per order (§10A.341) - so the toast says only what is certain: the
   * driver is on their way. Whether an SMS goes is the franchise's setting, not this screen's claim. */
  const goToAddress = usePatchOrdersOrderId({
    mutation: {
      onSuccess: () => {
        refresh()
        toast({ title: tt('Idete na adresu') })
      },
      // §9.385 - the server names a refusal, so let it.
      onError: (caught) =>
        toast({
          variant: 'destructive',
          title: tt('Stav sa nezmenil'),
          description: apiErrorMessage(
            caught,
            tt('Skúste to znova, alebo zavolajte dispečerovi.'),
          ),
        }),
    },
  })

  return (
    <div className="min-h-screen bg-background pb-10" data-driving>
      {/* ─── who and what, pinned to the top ─── */}
      <header className="safe-top bg-card border-b border-border sticky top-0 z-10">
        <div className="flex items-center gap-3 px-4 py-3">
          <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
            <Truck className="w-5 h-5 text-primary" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-bold leading-tight truncate">{wallet?.name ?? '-'}</p>
            <p className="text-xs text-muted-foreground truncate">
              {wallet === null ? '' : shiftLabel(wallet)}
              {wallet?.vehicleName != null && ` · ${wallet.vehicleName}`}
            </p>
          </div>
          <button
            type="button"
            data-refresh
            onClick={refresh}
            aria-label={tt('Obnoviť')}
            className="p-3 rounded-xl hover:bg-muted"
          >
            <RefreshCw className="w-5 h-5 text-muted-foreground" />
          </button>
          {/* §9.642, owner O41 - *"also one in the driver app"*. A rozvozár cannot reach the design screen,
            * which is behind a manager permission, so without this the only person who could change the
            * scheme on the surface read outdoors is the one not standing outdoors. Saved per employee, so a
            * driver choosing light does not lighten the till. */}
          <ThemeToggle className="p-3 rounded-xl hover:bg-muted text-muted-foreground" />
          {/* §9.162 - the way out, which this header did not have: the manager's sign-out lives in the sidebar
            * footer (§9.7.3) and this page is full-screen, so a driver who signed in here could not sign out - on
            * a phone handed between shifts, that is somebody else's session left open.
            *
            * §9.684, owner #9 - **alone in the corner now.** The icon-only *Aplikácia* beside it was the only way
            * into the rest of the system, and a tap aimed at one landed on the other. The ways in are the labelled
            * row below the header. */}
          <button
            type="button"
            data-driving-logout
            onClick={() => void logout()}
            aria-label={tt('Odhlásiť sa')}
            title={tt('Odhlásiť sa')}
            className="p-3 rounded-xl hover:bg-muted"
          >
            <LogOut className="w-5 h-5 text-muted-foreground" />
          </button>
        </div>

        {/* The run, from the deliveries themselves - never from the lifetime wallet.
          *
          * §9.384, FB-683 - **hidden, not deleted.** The owner: *"hide it for now, do not fully
          * remove it… drivers are reporting that its useless info (maybe it is, but maybe we will
          * give it back later)"*. The constant keeps the block compiled and type-checked against
          * `totals`, so turning it back on is one word; deleting it would mean writing it again
          * from memory. */}
        {SHOW_RUN_TOTALS && (
        <div className="grid grid-cols-3 divide-x divide-border border-t border-border" data-run-totals>
          <Cell label={tt('Zastávok')} value={formatCount(totals.stops)} />
          <Cell
            label={tt('Vyberiete')}
            value={formatCents(totals.collectCents)}
            tone="text-emerald-500"
          />
          <Cell label={tt('Zaplatené vopred')} value={formatCount(totals.alreadyPaid)} />
        </div>
        )}
      </header>

      <div className="px-4 pt-4 space-y-4 max-w-lg mx-auto">
        {/* §9.684, owner #9 - *"hornú listu kde sa vie preklikávať v systéme dať nižšie lebo je to úplne v Rohu a
          * ťažko sa nato kliKa"*. Below the header rather than in its corner, labelled rather than an icon, at
          * least 48 px tall, and each drawn only when the driver holds the permission its own route asks for
          * (`drivingExits`) - so nothing here opens on a lock panel. No row at all for a driver who may open
          * neither. */}
        {exits.length > 0 && (
          <nav
            aria-label={tt('Prejsť do systému')}
            data-driving-exits
            className={cn('grid gap-2', exits.length > 1 ? 'grid-cols-2' : 'grid-cols-1')}
          >
            {exits.map((exit) => (
              <Link
                key={exit.key}
                href={exit.path}
                data-driving-exit={exit.key}
                className="min-h-[48px] rounded-2xl bg-card border border-border px-3 text-sm font-bold flex items-center justify-center gap-2 hover:bg-muted"
              >
                {exit.key === 'dashboard' ? (
                  <LayoutDashboard className="w-5 h-5 text-primary shrink-0" />
                ) : (
                  <MapPin className="w-5 h-5 text-primary shrink-0" />
                )}
                <span className="truncate">{exit.label}</span>
              </Link>
            ))}
          </nav>
        )}

        {/* The shift switch is the only thing a driver can change about themselves here. */}
        {wallet !== null && (
          <button
            type="button"
            data-shift={wallet.isAvailable ? 'on' : 'off'}
            disabled={setShift.isPending}
            onClick={() => {
              buzz(navigator)
              setShift.mutate({ data: { isAvailable: !wallet.isAvailable } })
            }}
            className={cn(
              'w-full min-h-[48px] rounded-2xl font-bold flex items-center justify-center gap-2 border-2 transition-colors disabled:opacity-50',
              wallet.isAvailable
                ? 'border-emerald-500/40 text-emerald-500 bg-emerald-500/5'
                : 'border-border text-muted-foreground',
            )}
          >
            <Power className="w-5 h-5" />
            {wallet.isAvailable ? tt('Na smene - klepnutím ukončíte') : tt('Mimo smeny - klepnutím začnete')}
          </button>
        )}

        {/* §9.6: which van. `/trip-log` and `/delivery-statistics` attribute distance and fuel by vehicle,
          * so a driver left on last week's van puts this week's diesel on it. */}
        {!external && vehicles.length > 0 && (
          <div className="space-y-1.5" data-vehicle={vehicle?.id ?? 'none'}>
            <select
              value={vehicle === null ? '' : String(vehicle.id)}
              onChange={(event) =>
                setVehicle.mutate({
                  data: { vehicleId: event.target.value === '' ? null : Number(event.target.value) },
                })
              }
              aria-label={tt('Vozidlo')}
              data-vehicle-select
              disabled={setVehicle.isPending}
              className="w-full min-h-[48px] rounded-2xl bg-card border border-border px-3 text-sm font-semibold outline-none disabled:opacity-50"
            >
              <option value="">{tt('Bez vozidla')}</option>
              {vehicles.map((entry) => (
                <option key={entry.id} value={entry.id}>
                  {vehicleLabel(entry)}
                </option>
              ))}
            </select>
            <p className="text-[11px] text-muted-foreground" data-vehicle-note>
              {vehicleNote(vehicle)}
            </p>
          </div>
        )}

        {/* §9.639, FB-965 - **which map, chosen where the driver is.** §9.519 built this choice and put it on
          * `/profile`: reachable from here, and six steps away - the *Aplikácia* glyph, the dashboard, the
          * drawer, their own name, the *Osobné údaje* tab, a field between E-mail and Telefón, *Uložiť*, back.
          * On this franchise's data `map_app` is set on **0 of 15** drivers, which is what that distance looks
          * like from the database.
          *
          * **Beside the van rather than in the header**: this is the third thing a driver changes about
          * themselves, after the shift and the vehicle, and it is written in their shape rather than a new one.
          * The header's four controls are the way back and the way out, which is a different kind of thing.
          *
          * **Unconditional, unlike the vehicle picker above it.** That one is gated on `vehicles.length > 0`
          * because a franchise with no vans has nothing to offer; every franchise has maps, and the empty
          * option is always a real answer. */}
        <div className="space-y-1.5" data-driving-map={myMapApp ?? ''}>
          <select
            value={myMapApp ?? ''}
            onChange={(event) => setMapApp.mutate({ data: mapAppPayload(event.target.value) })}
            aria-label={tt('Mapa pre navigáciu')}
            data-driving-map-select
            disabled={setMapApp.isPending}
            className="w-full min-h-[48px] rounded-2xl bg-card border border-border px-3 text-sm font-semibold outline-none disabled:opacity-50"
          >
            {/* §9.512's lesson - the empty option has to exist, or a driver who chose once could never go
              * back to following the franchise. */}
            <option value="">{tt('Mapa podľa franšízy')}</option>
            {MAP_APPS.map((app) => (
              <option key={app} value={app}>
                {MAP_APP_LABELS[app]}
              </option>
            ))}
          </select>
          <p className="text-[11px] text-muted-foreground" data-driving-map-note>
            {mapChoiceNote(myMapApp, franchiseMapApp)}
          </p>
        </div>

        {/* §9.786 - the external courier's pool, his orders and his earnings. His delivery cards below are the
          * orders he is carrying, shown on *Moje*; the restaurant's own driver sees the page exactly as before. */}
        {external && (
          <CourierPanels tab={courierTab} onTab={setCourierTab} onTheRoad={deliveries.length} />
        )}

        {external && courierTab !== 'mine' ? null : deliveryQuery.isLoading ? (
          <div className="flex justify-center py-16">
            <Spinner />
          </div>
        ) : deliveries.length === 0 ? (
          external ? null : (
          <div className="text-center py-16 space-y-3" data-empty>
            <div className="w-16 h-16 bg-muted rounded-2xl flex items-center justify-center mx-auto">
              <CircleCheck className="w-9 h-9 text-muted-foreground" />
            </div>
            <p className="font-semibold">
              <T>Žiadne aktívne rozvozy</T>
            </p>
            <p className="text-sm text-muted-foreground max-w-xs mx-auto" data-empty-reason={emptyReason}>
              {emptyBoardNote(emptyReason)}
            </p>
          </div>
          )
        ) : (
          deliveries.map((delivery) => (
            <DeliveryCard
              mapApp={mapApp}
              onQr={
                offersQr(delivery.paymentMethod, qrActive, delivery.paymentStatus) ? () => setPaying(delivery) : null
              }
              key={delivery.id}
              delivery={delivery}
              minutes={minutesWaiting(delivery, openedAt)}
              busy={deliver.isPending || goToAddress.isPending}
              onGoToAddress={() => {
                buzz(navigator)
                goToAddress.mutate({ orderId: delivery.id, data: goingToAddressPayload() })
              }}
              onDeliver={() => {
                buzz(navigator)
                /* §9.609 - opened on what the customer declared when the order was taken, so a
                 * customer who does what they said costs one tap and only a change is a change.
                 * §9.751 - through the rule, which opens an unconfirmed online order on nothing. */
                setPaidBy(doorPaymentPreselect(delivery))
                setConfirming(delivery)
              }}
              onHandBack={
                // Only from the field - the same condition the server enforces, so the button is
                // absent rather than refused.
                mayHandBack(delivery.status)
                  ? () => {
                      buzz(navigator)
                      setHandingBack(delivery)
                    }
                  : null
              }
            />
          ))
        )}

        {/* The lifetime figures, labelled as lifetime - and the way to the day that is actually being
          * asked about at handover. */}
        {wallet !== null && (
          <p className="text-[11px] text-muted-foreground pt-2" data-wallet-note>
            {walletNote(wallet)}
          </p>
        )}
        <Link
          href="/wallet"
          data-wallet-link
          className="flex items-center justify-between gap-2 min-h-[48px] rounded-2xl bg-card border border-border px-4 text-sm font-semibold hover:bg-muted"
        >
          <span className="flex items-center gap-2">
            <Wallet className="h-4 w-4 text-primary" />
            <T>Peňaženka - čo odovzdávam</T>
          </span>
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        </Link>
      </div>

      {paying !== null && (
        /* §9.338 - the bank answers now, so there is no *Zaplatil / Nezaplatil* pair to press.
           The sheet opens a real TatraPay session and shows the server's countdown. */
        <TransferPaymentSheet
          orderId={paying.id}
          orderNumber={paying.orderNumber}
          onClose={() => setPaying(null)}
          onPaid={refresh}
        />
      )}

      <ConfirmDialog
        open={confirming !== null}
        onClose={() => setConfirming(null)}
        title={tt('Označiť ako doručenú?')}
        description={
          confirming === null
            ? ''
            : `#${confirming.orderNumber} · ${collectLabel(confirming)}. ${tt('Toto sa nedá vrátiť - stav uvidí kuchyňa aj dispečer.')}`
        }
        confirmLabel={tt('Doručené')}
        loading={deliver.isPending}
        /* §9.610 - refused until the driver chooses, and only where there is nothing to preselect. A blank
         * method carries no declaration to open on, and §9.579's rule is that a dialog must not open on a
         * value nobody chose and send it as a choice. Where the order does carry one, the choice is already
         * made and this is false, so the common case is still one tap. */
        confirmDisabled={
          confirming !== null &&
          doorChoices(confirming).length > 0 &&
          !doorChoices(confirming).includes(paidBy)
        }
        onConfirm={() => {
          const delivery = confirming
          if (delivery === null) return
          deliver.mutate({ orderId: delivery.id, data: deliveredPayload(delivery, paidBy) })
        }}
      >
        {/* §9.609, FB-916 - *"if they payed by card kurier that we will actually switch it and will not keep
          * it as hotovosť"*. Absent for an order already settled online, which `doorPaymentChoices` answers
          * with an empty list: that money is not the driver's to relabel. */}
        {confirming !== null && doorChoices(confirming).length > 0 && (
          <div className="space-y-2" data-paid-by>
            <p className="text-xs font-semibold text-muted-foreground">
              <T>Čím zaplatil?</T>
            </p>
            <div className="grid grid-cols-2 gap-2">
              {doorChoices(confirming).map((method) => (
                <button
                  key={method}
                  type="button"
                  data-paid-by-option={method}
                  data-chosen={String(paidBy === method)}
                  onClick={() => setPaidBy(method)}
                  className={cn(
                    'min-h-[48px] rounded-xl border px-3 text-sm font-bold',
                    // §9.751 - an unconfirmed order's own online method is *I took nothing*, a different kind
                    // of answer from the two ways of paying at the door, so it takes the whole row above them.
                    isBankSettled(method) && 'col-span-2',
                    paidBy === method
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border text-foreground',
                  )}
                >
                  {doorChoiceLabel(method)}
                </button>
              ))}
            </div>
            {/* §9.751, FB-1067 - a live receipt or a payment the bank took keeps the method at the close
              * (§10A.304), so a different choice is said to be one the server will not save, before the tap
              * rather than after it. */}
            {keepsNote !== '' && (
              <p className="text-xs text-amber-600 flex items-start gap-1.5" data-choice-kept>
                <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" />
                {keepsNote}
              </p>
            )}
          </div>
        )}
      </ConfirmDialog>

      {/* §9.370, FB-658 - the customer refused it. `quality_check` rather than `cancelled`: the food
        * exists and somebody has to decide what happens to it, and cancelling is a money decision an
        * admin makes, not a driver at the door. */}
      <ConfirmDialog
        open={handingBack !== null}
        onClose={() => setHandingBack(null)}
        title={tt('Vrátiť objednávku späť?')}
        description={handingBack === null ? '' : handBackNote(handingBack.orderNumber)}
        confirmLabel={tt('Vrátiť späť')}
        loading={deliver.isPending}
        onConfirm={() => {
          const delivery = handingBack
          if (delivery === null) return
          deliver.mutate({
            orderId: delivery.id,
            // The server permits this for a driver only from the field, which is why the button is
            // not offered anywhere else.
            data: { newStatus: 'quality_check', reason: handBackReason() },
          })
        }}
      />
    </div>
  )
}

function Cell({ label, value, tone }: { label: string; value: string; tone?: string }) {
  return (
    <div className="px-3 py-2.5 text-center">
      <p className="text-[11px] text-muted-foreground">{label}</p>
      <p className={cn('font-bold text-lg leading-tight tabular-nums', tone)}>{value}</p>
    </div>
  )
}

function DeliveryCard({
  delivery,
  minutes,
  busy,
  mapApp,
  onGoToAddress,
  onDeliver,
  onHandBack,
  onQr,
}: {
  delivery: Delivery
  minutes: number
  busy: boolean
  /**
   * The map this franchise sends its drivers to, or null for no preference - §9.518, FB-801.
   *
   * **Threaded rather than read here**, because this component renders once per delivery and the setting
   * is one fact about the franchise: fetching it per card would be a request per bag.
   */
  mapApp: string | null
  /** §9.682 - *Ísť na adresu*, offered while the order is `on_the_way` (`doorStep`). */
  onGoToAddress: () => void
  onDeliver: () => void
  /**
   * §9.370 - the customer refused the food at the door.
   *
   * Offered only while the order is out in the field, because that is the only state the server
   * lets a driver hand one back from (`IN_THE_FIELD`).
   */
  onHandBack: (() => void) | null
  /** Null where there is nothing left to transfer — an order paid online or on a terminal. */
  onQr: (() => void) | null
}) {
  const urgency = urgencyOf(minutes)
  // §9.504 - the whole delivery, so the resolved point wins over the address text where there is one.
  const maps = navigationUrl(delivery, mapApp)
  const phone = telUrl(delivery.customerPhone)
  /**
   * §9.384, FB-683 - *"make sure, that whole objednávka can be collapsed"*.
   *
   * **Collapsed by default since §9.387**, on the owner's instruction: *"by default make all
   * objednávky collapsed in driver app"*. §9.384 chose expanded and argued for it - *"a driver who
   * has pressed nothing sees the card they saw yesterday"* - which is a good argument about not
   * surprising people and the wrong one about what a driver needs: a list of doors. Four stops on a
   * phone means scrolling past stop one's items to reach stop two.
   * `agos-a-recorded-decision-can-still-be-wrong`.
   *
   * Still **per card** and still not remembered between visits: a collapse is an answer about *this*
   * door, and a setting that survived a reload would hide an address somebody forgot they hid.
   */
  const [collapsed, setCollapsed] = useState(true)
  const method = paymentMethodBadge(delivery.paymentMethod)

  return (
    <article
      className={cn(
        'bg-card border-2 rounded-2xl overflow-hidden',
        urgency === 'late'
          ? 'border-red-500/50'
          : urgency === 'watch'
            ? 'border-amber-500/50'
            : 'border-violet-500/40',
      )}
      data-delivery={delivery.orderNumber}
      data-urgency={urgency}
    >
      <div className="px-4 pt-4 pb-3 space-y-2.5">
        {/* §9.384, FB-683 - **the two things a driver reads at a door, first and large.** The owner:
          * *"číslo objednávky, typ platby (Hotovosť, Karta) make sure, those are bigger, important
          * things for driver."* The number was `text-xs`, the smallest type on the screen, and the
          * method was an unstyled line under the total. */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            data-collapse={delivery.orderNumber}
            aria-expanded={!collapsed}
            aria-label={collapsed ? tt('Rozbaliť') : tt('Zbaliť')}
            onClick={() => setCollapsed((shown) => !shown)}
            className="p-1 -ml-1 rounded-lg text-muted-foreground hover:bg-muted shrink-0"
          >
            {collapsed ? <ChevronDown className="w-5 h-5" /> : <ChevronUp className="w-5 h-5" />}
          </button>
          <span className="font-mono text-2xl font-black text-primary leading-none">
            #{delivery.orderNumber}
          </span>
          {/* The board's own vocabulary and tones - *"match colors from Rozvoz nástenka"*. */}
          {method !== null && (
            <span
              data-payment-method={delivery.paymentMethod}
              className={cn(
                'text-sm font-bold px-2.5 py-1 rounded-lg border ml-auto',
                method.tone,
              )}
            >
              {method.label}
            </span>
          )}
        </div>

        {/* §9.786, BACKEND.md §10A.438 - an external courier's pay for this order: *"apka musí ukazovať aj odmenu
          * externého šoféra"*. Folded or not, because it is the figure he took the order for. */}
        {delivery.rewardCents !== null && (
          <p className="text-sm font-bold text-emerald-500" data-delivery-reward={delivery.rewardCents}>
            <T>Vaša odmena</T> {formatCents(delivery.rewardCents)}
          </p>
        )}

        {/* Collapsed, this is the whole card above the buttons - §9.387, and the owner named what
          * belongs here: *"we have price under objednávka, thats cool, but remove «Vyberte 15,50 e»
          * and add adresa there"*. The collect badge said the total a second time; the address is
          * the one thing that says where to go. */}
        {collapsed && (
          <div className="space-y-1" data-collapsed-summary>
            <div className="flex items-baseline justify-between gap-2">
              <span className="font-bold text-2xl tabular-nums">
                {formatCents(delivery.totalCents)}
              </span>
              {/* The one thing the total cannot say: that there is nothing to take at this door.
                * Short, because it is beside an address on a phone. */}
              {collectOutcome(delivery).kind === 'settled' && (
                <span
                  className="text-[11px] font-semibold px-2 py-0.5 rounded-lg bg-blue-500/15 text-blue-500 shrink-0"
                  data-collapsed-paid
                >
                  <T>zaplatené</T>
                </span>
              )}
              {/* §9.751 - an unconfirmed payment's word where the chip was, in the badge's amber, so a folded
                * card no longer shows a CardPay the bank never confirmed only in the paid-online blue. */}
              {/* §9.766 - a customer who pays by QR at this door: the header's *QR Pay* is in the paid-online blue, so
                * the folded card says the money is not in yet, under a marker of its own. */}
              {qrChip(collectOutcome(delivery)) !== '' && (
                <span
                  className={cn(
                    'text-[11px] font-semibold px-2 py-0.5 rounded-lg shrink-0',
                    collectTone(collectOutcome(delivery)),
                  )}
                  data-collapsed-qr
                >
                  {qrChip(collectOutcome(delivery))}
                </span>
              )}
              {unconfirmedChip(collectOutcome(delivery)) !== '' && (
                <span
                  className={cn(
                    'text-[11px] font-semibold px-2 py-0.5 rounded-lg shrink-0',
                    collectTone(collectOutcome(delivery)),
                  )}
                  data-collapsed-unconfirmed
                >
                  {unconfirmedChip(collectOutcome(delivery))}
                </span>
              )}
            </div>
            {delivery.customerAddress !== null && (
              <p className="flex items-start gap-1.5 text-sm font-medium" data-collapsed-address>
                <MapPin className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
                {delivery.customerAddress}
              </p>
            )}
          </div>
        )}

        {!collapsed && (
          <div className="flex items-center gap-2">
            <span className="text-[11px] px-2 py-0.5 rounded-lg bg-muted text-muted-foreground">
              {statusLabel(delivery.status)}
            </span>
            <span className="text-xs text-muted-foreground flex items-center gap-1 ml-auto" data-elapsed>
              <Clock className="w-3 h-3" />
              {elapsedLabel(minutes)}
            </span>
          </div>
        )}

        {!collapsed && delivery.customerName !== null && (
          <p className="font-bold flex items-center gap-2 text-base">
            <User className="w-4 h-4 text-muted-foreground shrink-0" /> {delivery.customerName}
          </p>
        )}

        {!collapsed && delivery.customerAddress !== null && (
          <p className="flex items-start gap-2 text-sm font-medium">
            <MapPin className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
            {delivery.customerAddress}
          </p>
        )}

        {!collapsed && delivery.notes !== null && (
          <p className="text-xs text-amber-500 flex items-start gap-1.5" data-note>
            <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-px" />
            {delivery.notes}
          </p>
        )}

        {!collapsed && delivery.items.length > 0 && (
          <p className="text-xs text-muted-foreground">
            {delivery.items
              .map((item) => `${formatCount(item.quantity)}× ${item.productName}`)
              .join(' · ')}
          </p>
        )}

        {/* What to take at the door, said plainly - a driver should not be working it out from a
          * total. Expanded only: collapsed draws the same two above. */}
        {!collapsed && (
        <div className="flex items-center justify-between pt-1">
          <span className="font-bold text-lg tabular-nums">{formatCents(delivery.totalCents)}</span>
          <span
            className={cn(
              'text-xs font-semibold px-2 py-1 rounded-lg',
              /* §9.610 - the outcome, not the label's first word. This read
                 * `collectLabel(delivery).startsWith(tt('Vyberte'))`, so the colour was keyed on the first
                 * word of a Slovak sentence: reword the label and the styling changed silently, which is
                 * `notificationSubject`'s shape (§9.520).
                 * §9.751 - through `collectTone`, an exhaustive switch. The ternary that was here ended in
                 * blue, the colour of *paid*, for every outcome it did not name - which an unconfirmed online
                 * payment would have been. */
              collectTone(collectOutcome(delivery)),
            )}
            data-collect
            data-collect-outcome={collectOutcome(delivery).kind}
          >
            {collectLabel(delivery)}
          </span>
        </div>
        )}
      </div>

      {/* Two taps a driver makes with a bag in the other hand: navigate, and call. */}
      <div className="grid grid-cols-2 divide-x divide-border border-t border-border">
        <a
          href={maps ?? undefined}
          target="_blank"
          rel="noreferrer"
          data-navigate={maps === null ? 'none' : 'maps'}
          aria-disabled={maps === null}
          className={cn(
            'min-h-[48px] flex items-center justify-center gap-2 text-sm font-bold',
            maps === null ? 'text-muted-foreground/40 pointer-events-none' : 'text-primary',
          )}
        >
          <Navigation className="w-4 h-4" /> <T>Navigovať</T>
        </a>
        <a
          href={phone ?? undefined}
          // §9.381, FB-676 - **the same two attributes the navigate link beside it has had all
          // along.** The owner: *"when driver is there, he will use his phone to call, so he will
          // not get odhlásiť… so he can still work in the driver app."* A `tel:` href with no
          // target navigates **this** document: on a phone with the app on the home screen the
          // document is unloaded for the hand-off, and the driver comes back to a cold boot - which
          // is what being logged out looks like from the pavement. The asymmetry between these two
          // links, in one `<div>`, is what says it was an oversight rather than a decision.
          target="_blank"
          rel="noreferrer"
          data-call={phone === null ? 'none' : 'tel'}
          aria-disabled={phone === null}
          className={cn(
            'min-h-[48px] flex items-center justify-center gap-2 text-sm font-bold',
            phone === null ? 'text-muted-foreground/40 pointer-events-none' : 'text-blue-500',
          )}
        >
          <Phone className="w-4 h-4" /> <T>Zavolať</T>
        </a>
      </div>

      {/* §9.656, FB-985 - **on a collapsed card too**, which is the owner's report: *"we want to show that QR
        * Pay also when its hotovosť"*. The gate was already open for cash - `offersQr` is
        * `cash || card_courier || ''` and so is the server's `OWES_MONEY` - and what hid it was this very
        * condition. §9.384 put the button behind the collapse calling it *"a deliberate action rather than the
        * three a driver repeats"*, which was a one-tap reach until §9.387 made every card collapsed by default on
        * the owner's own instruction. Two taps at a door with a customer holding a phone is the report.
        *
        * Only when `onQr` is non-null, so it appears on an order that **owes money** and the collapsed card does
        * not grow for the ones already paid - `offersQr`'s own answer rather than a second rule.
        *
        * **And a braced comment rather than a bare block one**: in JSX a block comment outside braces is rendered
        * as literal text, so the first draft of this note would have printed itself on a driver's card. `eslint`
        * was the only gate that saw it - `tsc` and 127 passing cases both let it through. And the second draft
        * did not parse, because spelling the braced form out puts a comment terminator inside the comment. */}
      {onQr !== null && (
        <button
          type="button"
          data-qr-open={delivery.orderNumber}
          onClick={onQr}
          className="w-full min-h-[48px] border-t border-border text-sm font-bold flex items-center justify-center gap-2 text-primary hover:bg-muted transition-colors"
        >
          {/* §9.741 - *QR Pay*, the owner's word, as the notice that it was paid already says. */}
          <QrCode className="w-4 h-4" /> <T>Zaplatiť cez QR Pay</T>
        </button>
      )}

      {/* §9.682, owner #23b - *"dal že na cestu a potom až doručená"*: set off, and only then delivered. One
        * button whose action follows the status, as legacy's was (`deliveries.tsx:273-280`), because a close
        * offered beside the first step is the one a driver presses instead of it - and then the customer hears
        * nothing. Orange is legacy's own colour for this step. */}
      {doorStep(delivery.status) === 'go' ? (
        <button
          type="button"
          data-go-to-address={delivery.orderNumber}
          disabled={busy}
          onClick={onGoToAddress}
          className="w-full min-h-[52px] bg-orange-500 hover:bg-orange-600 text-white font-bold text-sm flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
        >
          <MapPin className="w-5 h-5" /> <T>Ísť na adresu</T>
        </button>
      ) : (
        <button
          type="button"
          data-deliver={delivery.orderNumber}
          disabled={busy}
          onClick={onDeliver}
          className="w-full min-h-[52px] bg-emerald-500 hover:bg-emerald-600 text-white font-bold text-sm flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
        >
          <CircleCheck className="w-5 h-5" /> <T>Označiť ako doručenú</T>
        </button>
      )}

      {/* §9.370, FB-658 - the flow nobody had: the customer will not take it. The server has allowed
        * a driver to hand an order back since `IN_THE_FIELD` was written; nothing could reach it. */}
      {!collapsed && onHandBack !== null && (
        <button
          type="button"
          data-hand-back={delivery.orderNumber}
          disabled={busy}
          onClick={onHandBack}
          className="w-full min-h-[48px] border-t border-border text-sm font-bold flex items-center justify-center gap-2 text-amber-600 hover:bg-amber-500/10 transition-colors disabled:opacity-50"
        >
          <Undo2 className="w-4 h-4" /> <T>Vrátiť späť</T>
        </button>
      )}
    </article>
  )
}
